import { Button, Stack, styled } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import {
  type GridSelectionModel,
  type GridColDef,
  type GridRenderCellParams,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { type MonitoringDataGridRow } from "../types";
import { DataGrid } from "./DataGrid";
import { type NavigateFunction } from "react-router-dom";
import { useState } from "react";
import { CustomToolbar } from "./CustomToolbar";
import { useGridPagination } from "../hooks/useGridPagination";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";

interface IProps {
  handleRowSelectionChange: (newSelectionModel: GridSelectionModel) => void;
  navigate: NavigateFunction;
  rows: MonitoringDataGridRow[];
  setSelectedIds: React.Dispatch<React.SetStateAction<GridSelectionModel>>;
  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>;
  disabledAcquireMultipleBooksButton: boolean;
  handleClickAcquireButton: () => void;
}

const StyledDataGrid = styled(DataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  navigate: NavigateFunction,
  setSelectedIds: React.Dispatch<React.SetStateAction<GridSelectionModel>>,
  setAcquireBookOpen: React.Dispatch<React.SetStateAction<boolean>>,
  isReadOnly: boolean
): GridColDef[] => {
  return [
    {
      field: "realEstateType",
      headerName: "不動産種別",
      width: 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "prefectureName",
      headerName: "都道府県",
      width: 100,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "location",
      headerName: "所在",
      width: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      width: 220,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "registrationDate",
      headerName: "登録日",
      width: 120,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value != null) return format(new Date(value), "yyyy/MM/dd");
      },
    },
    {
      field: "remarks",
      headerName: "備考",
      width: 250,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "requestDate",
      headerName: "法務局受付日",
      width: 120,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      align: "left",
      headerAlign: "left",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        if (value != null) return format(new Date(value), "yyyy/MM/dd");
      },
    },
    {
      field: "receptionReason",
      headerName: "登記原因",
      width: 200,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "outside",
      headerName: "外",
      width: 50,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
      disableExport: true,
      renderCell: ({
        row,
      }: GridRenderCellParams<string, MonitoringDataGridRow>) => {
        return (
          <Stack direction={"row"} spacing={1}>
            <Button
              onClick={() => {
                navigate(`/monitoring/detail/${row.id}`);
              }}
              sx={{
                bgcolor: "#F4A150",
                "&:hover": {
                  backgroundColor: "#DF9B4A",
                },
              }}
              variant="contained"
            >
              モニタリング結果
            </Button>
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <Button
                onClick={() => {
                  setSelectedIds([row.id]);
                  setAcquireBookOpen(true);
                }}
                variant="contained"
                disabled={isReadOnly}
              >
                登記取得
              </Button>
            </ReadOnlyTooltip>
          </Stack>
        );
      },
    },
  ];
};

/**
 * モニタリング結果の表部分
 * @param {IProps} props
 * @returns
 */
export const MonitoringResultDataGrid: React.FC<IProps> = (props: IProps) => {
  const {
    handleRowSelectionChange,
    navigate,
    rows,
    setSelectedIds,
    setAcquireBookOpen,
    disabledAcquireMultipleBooksButton,
    handleClickAcquireButton,
  } = props;
  const isReadOnly = useReadOnlyUser();
  const colDef = makeGridColDef(
    navigate,
    setSelectedIds,
    setAcquireBookOpen,
    isReadOnly
  );
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const rowsPerPageOptions: number[] = [10, 50, 100];

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  return (
    <StyledDataGrid
      autoHeight
      columns={colDef}
      disableColumnMenu
      disableSelectionOnClick={true}
      checkboxSelection
      onSelectionModelChange={handleRowSelectionChange}
      rows={rows}
      pageSize={pageSize}
      rowCount={rows.length}
      page={page}
      rowsPerPageOptions={rowsPerPageOptions}
      onPageChange={changePage}
      onPageSizeChange={changePageSize}
      sortModel={sortModel}
      onSortModelChange={(model) => {
        setSortModel(model);
      }}
      components={{
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        toolbar: {
          page,
          pageSize,
          allCount: rows.length,
          rowsPerPageOptions,
          disabledAcquireMultipleBooksButton,
          changePage,
          changePageSize,
          handleClickAcquireButton,
        },
      }}
    />
  );
};
